/**
 * Publishes the announcement bar's height as --dom-bar-height on <body>.
 *
 * The bar is an Attrac app block sitting at z-index 200; the menu drawer sits
 * at 18. So the bar paints over the top strip of the drawer, and whatever the
 * drawer puts up there - the close button - ends up half hidden behind it.
 *
 * Measuring rather than hardcoding, because the bar is not a fixed height: its
 * copy wraps to a second line on a narrow phone, roughly doubling it. A
 * constant would clear the bar at one width and leave a gap or an overlap at
 * another.
 *
 * The bar is injected by app JS after this file runs, so we watch for it rather
 * than looking once. Everything degrades to 0px, which is exactly the layout
 * that existed before this file, so a failure here is invisible rather than
 * broken.
 */
(() => {
  const SELECTOR = '.aph_bar_bar, [class*="aph_bar"]';
  const root = document.body;
  let bar = null;

  const publish = () => {
    const h = bar ? Math.round(bar.getBoundingClientRect().height) : 0;
    root.style.setProperty('--dom-bar-height', `${h}px`);
  };

  // Re-measure on resize as well as on mutation: a rotation or a font swap can
  // rewrap the copy without the DOM changing at all.
  const ro = 'ResizeObserver' in window ? new ResizeObserver(publish) : null;

  const attach = (el) => {
    if (!el || el === bar) return;
    if (bar && ro) ro.unobserve(bar);
    bar = el;
    if (ro) ro.observe(bar);
    publish();
  };

  // The app nests the bar inside wrappers that share its class prefix and
  // collapse to zero height, and querySelector would hand back whichever comes
  // first in the document. Take the tallest match instead - that is the strip
  // actually covering the drawer.
  const find = () => {
    let best = null;
    let bestH = 0;
    document.querySelectorAll(SELECTOR).forEach((el) => {
      const h = el.getBoundingClientRect().height;
      if (h > bestH) { bestH = h; best = el; }
    });
    if (best) attach(best);
    else if (bar) { bar = null; publish(); } // bar dismissed by the visitor
  };

  find();
  addEventListener('resize', publish);
  addEventListener('load', find);

  // The app can inject, re-render, or remove the bar at any point.
  new MutationObserver(find).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  // Stop watching after things have settled. The observer above is cheap but it
  // runs on every DOM change on the page, and the bar is not going to appear for
  // the first time ten seconds in.
  setTimeout(() => {
    find();
  }, 10000);
})();
